import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable, Platform, TextInput, ActivityIndicator, TouchableOpacity } from 'react-native';
import {
  ManageAccountsSearchBar,
  DEFAULT_ACCOUNT_BLOCKSIZE,
  type AccountFilterOption,
  type ManageAccountsSearchValues,
} from './ManageAccountsSearchBar';
import {
  SkinTypeDefStorage,
  SKIN_TYPE_DEF_SCHEMA_VERSION,
  type SkinSettingDto,
} from '../../../storage/SkinTypeDefStorage';
import { ensureBundledSkinAssets } from '../../../services/skin/resolveSkinChannelInfo';
import { isOvershotEmptyPage } from '../../../services/clients/ClientSearchEngine';
import { ProviderAccountService } from '../../../services/ProviderAccountService';
import { AccountPracInfoService } from '../../../services/account/AccountPracInfoService';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import type { PracSearchRequestDto, PracSearchResultDto, PracCreateRequestDto, AccountPracInfoRequestDto } from '../../../types/api';
import { useActiveAccountStore } from '../../../stores/activeAccountStore';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { resolveAuthContext } from '../../../services/auth/authContext';

const isTruthyFlag = (value: string | null | undefined): boolean => {
  const normalized = (value ?? '').trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes';
};

export interface AccountSearchResult {
  accountId: string;
  accountName: string;
  ownerEmail: string;
  accountGroup: string;
  billpayProcessor: string;
  status: string;
  isParent: boolean;
}

interface ManageAccountsViewProps {
  // Props for API integration - to be implemented
  apiService?: any;
  storage?: any;
}

// Global registry for account selector options
const getAccountSelectorOptionsRegistry = (): { options: Array<{ accountId: string; dname: string }> } => {
  const globalScope = globalThis as any;
  if (!globalScope.__proGroupsAccountSelectorOptions) {
    globalScope.__proGroupsAccountSelectorOptions = { options: [] };
  }
  return globalScope.__proGroupsAccountSelectorOptions;
};

interface AccountPagingState {
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
}

// Global registry for persisting search results (and their paging state) across navigation
const getSearchResultsRegistry = (): {
  results: AccountSearchResult[];
  timestamp: number;
  paging?: AccountPagingState;
  searchedValues?: ManageAccountsSearchValues;
} => {
  const globalScope = globalThis as any;
  if (!globalScope.__proGroupsAccountSearchResults) {
    globalScope.__proGroupsAccountSearchResults = { results: [], timestamp: 0 };
  }
  return globalScope.__proGroupsAccountSearchResults;
};

/**
 * Reads the current "account selection list" — the most recent Manage Accounts search
 * results, persisted across navigation via the same global registry ManageAccountsView
 * itself restores from on mount. Used by AccountSettingsView's "Parent" dropdown (filtered
 * to isParent accounts) so it doesn't need its own independent account search.
 */
export const getManagedAccountSearchResults = (): AccountSearchResult[] => {
  return getSearchResultsRegistry().results;
};

const updateAccountSelectorOptions = (searchResults: AccountSearchResult[]) => {
  const registry = getAccountSelectorOptionsRegistry();
  registry.options = searchResults.map(result => ({
    accountId: result.accountId,
    dname: result.accountName,
  }));
  
  // Dispatch event to notify ProGroupsSubmenuBar of updated options
  if (typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('progroups:account-selector-options-updated'));
  }
};

export const ManageAccountsView: React.FC<ManageAccountsViewProps> = ({
  apiService,
  storage,
}) => {
  const [searchValues, setSearchValues] = useState<ManageAccountsSearchValues>({
    accountName: '',
    accountId: '',
    accountGroup: '',
    billpayProcessor: '',
    showAll: false,
    blocksize: DEFAULT_ACCOUNT_BLOCKSIZE,
  });
  // The values used for the last Search click; Prev/Next page with these, not with unsearched edits.
  const [searchedValues, setSearchedValues] = useState<ManageAccountsSearchValues | null>(null);
  const [paging, setPaging] = useState<AccountPagingState>({
    blocknum: '0',
    blocksize: DEFAULT_ACCOUNT_BLOCKSIZE,
    hasMore: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<AccountSearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Add New Account form state
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [newAccountPrototype, setNewAccountPrototype] = useState('');
  const [newAccountContactName, setNewAccountContactName] = useState('');
  const [newAccountContactEmail, setNewAccountContactEmail] = useState('');
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountReturnWebsite, setNewAccountReturnWebsite] = useState('');
  const [isSubmittingAccount, setIsSubmittingAccount] = useState(false);
  const [accountFormError, setAccountFormError] = useState<string | null>(null);
  
  // Get active account from store
  const activeAccountId = useActiveAccountStore(state => state.activeAccountId);
  const setActiveAccount = useActiveAccountStore(state => state.setActiveAccount);
  const canSwitchAccount = useActiveAccountStore(state => state.canSwitchAccount);

  // Account Group / Billpay Processor filter lists come from the synced SkinTypeDefs (every stored
  // skin, de-duplicated by value).
  const [accountGroupOptions, setAccountGroupOptions] = useState<AccountFilterOption[]>([]);
  const [billpayProcessorOptions, setBillpayProcessorOptions] = useState<AccountFilterOption[]>([]);

  useEffect(() => {
    if (!storage) {
      return;
    }

    let cancelled = false;

    const toOptions = (lists: SkinSettingDto[][]): AccountFilterOption[] => {
      const byValue = new Map<string, AccountFilterOption>();
      lists.flat().forEach((entry) => {
        const value = (entry.value || '').trim();
        if (value && !byValue.has(value)) {
          byValue.set(value, { value, label: (entry.name || '').trim() || value });
        }
      });
      return Array.from(byValue.values());
    };

    void (async () => {
      try {
        const skinDefStorage = new SkinTypeDefStorage(storage);
        let defs = await skinDefStorage.getAllSkinTypeDefs();
        const isStale = defs.length === 0 || defs.some(def => (def.schemaVersion ?? 0) < SKIN_TYPE_DEF_SCHEMA_VERSION);
        if (isStale && Platform.OS === 'web') {
          try {
            await ensureBundledSkinAssets(storage);
            defs = await skinDefStorage.getAllSkinTypeDefs();
          } catch (bundleError) {
            console.warn('ManageAccountsView: failed to load bundled skin assets', bundleError);
          }
        }
        if (cancelled) {
          return;
        }
        setAccountGroupOptions(toOptions(defs.map(def => def.accountGroups ?? [])));
        setBillpayProcessorOptions(toOptions(defs.map(def => def.billpayProcessors ?? [])));
      } catch (loadError) {
        console.warn('ManageAccountsView: failed to load account filter lists', loadError);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storage]);

  // Restore search results from registry on mount
  useEffect(() => {
    const registry = getSearchResultsRegistry();
    if (registry.results.length > 0) {
      setSearchResults(registry.results);
      setHasSearched(true);
      if (registry.paging) setPaging(registry.paging);
      if (registry.searchedValues) {
        setSearchedValues(registry.searchedValues);
        setSearchValues(registry.searchedValues);
      }
     }
  }, []);

  // Persist search results to registry whenever they change
  useEffect(() => {
    if (searchResults.length > 0) {
      const registry = getSearchResultsRegistry();
      registry.results = searchResults;
      registry.paging = paging;
      registry.searchedValues = searchedValues ?? undefined;
      registry.timestamp = Date.now();
     }
  }, [searchResults, paging, searchedValues]);

  const handleSearch = async (values: ManageAccountsSearchValues, blocknum: string = '0') => {
    setIsLoading(true);
    setHasSearched(true);
    setError(null);

    try {
      if (!apiService) {
        throw new Error('API service not available');
      }

      const accountService = new ProviderAccountService(apiService);
      const blocksize = values.blocksize || DEFAULT_ACCOUNT_BLOCKSIZE;

      // Build search request
      const searchRequest: PracSearchRequestDto = {
        blocknum,
        blocksize,
        searchTerms: [],
        searchFilters: [],
      };

      // Add search terms based on input values
      if (values.accountName.trim()) {
        searchRequest.searchTerms.push({
          searchField: 'accountname',
          searchTerm: values.accountName.trim(),
        });
      }

      if (values.accountId.trim()) {
        searchRequest.searchTerms.push({
          searchField: 'accountid',
          searchTerm: values.accountId.trim(),
        });
      }

      if (values.accountGroup.trim()) {
        searchRequest.searchTerms.push({
          searchField: 'accountgroup',
          searchTerm: values.accountGroup.trim(),
        });
      }

      if (values.billpayProcessor.trim()) {
        searchRequest.searchTerms.push({
          searchField: 'billpayprocessor',
          searchTerm: values.billpayProcessor.trim(),
        });
      }

      // Add show all filter if needed
      if (values.showAll) {
        searchRequest.searchFilters.push({
          searchField: 'showall',
          searchFilter: '1',
        });
      }

      // Call the API
      const response = await accountService.searchAccounts(searchRequest);

      // Map response to AccountSearchResult format
      const results: AccountSearchResult[] = response.results.map((result: PracSearchResultDto) => ({
        accountId: result.AccountId,
        accountName: result.DName || `Account ${result.AccountId}`,
        ownerEmail: result.Email || '-',
        accountGroup: result.AccountGroup || '-',
        billpayProcessor: result.BillpayProcessor || '-',
        status: result.Status || '-',
        isParent: isTruthyFlag(result.IsParent),
      }));

      // Paged past the last block: keep the last good page and just disable Next.
      if (isOvershotEmptyPage(blocknum, results.length)) {
        setPaging(current => ({ ...current, hasMore: false }));
        return;
      }

      setSearchResults(results);
      setSearchedValues(values);
      setPaging({ blocknum, blocksize, hasMore: results.length >= parseInt(blocksize, 10) });

      // Update account selector dropdown options
      updateAccountSelectorOptions(results);
    } catch (err: any) {
      console.error('ManageAccountsView: Search failed', err);
      setError(err?.message || 'Search failed');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextPage = () => {
    if (!searchedValues) return;
    handleSearch(searchedValues, String(parseInt(paging.blocknum, 10) + 1));
  };

  const handlePrevPage = () => {
    if (!searchedValues) return;
    handleSearch(searchedValues, String(Math.max(0, parseInt(paging.blocknum, 10) - 1)));
  };

  const handleAddNewAccount = () => {
    setIsAddingAccount(true);
    setAccountFormError(null);
    
    // Initialize prototype dropdown with first available option from search results or active account
    if (!newAccountPrototype) {
      if (searchResults.length > 0) {
        setNewAccountPrototype(searchResults[0].accountId);
      } else if (activeAccountId) {
        setNewAccountPrototype(activeAccountId);
      }
    }
  };

  const handleCancelAddAccount = () => {
    setIsAddingAccount(false);
    setNewAccountPrototype('');
    setNewAccountContactName('');
    setNewAccountContactEmail('');
    setNewAccountName('');
    setNewAccountReturnWebsite('');
    setAccountFormError(null);
  };

  const handleSubmitAddAccount = async () => {
    // Validation
    if (!newAccountName.trim()) {
      setAccountFormError('Account Name is required');
      return;
    }
    
    if (!newAccountContactEmail.trim()) {
      setAccountFormError('Contact Email is required');
      return;
    }

    if (!newAccountPrototype) {
      setAccountFormError('Prototype account is required');
      return;
    }

    if (!apiService) {
      setAccountFormError('API service not available');
      return;
    }

    setIsSubmittingAccount(true);
    setAccountFormError(null);

    try {
      const accountService = new ProviderAccountService(apiService);
      
      const createRequest: PracCreateRequestDto = {
        ProtoId: newAccountPrototype,
        ContactName: newAccountContactName.trim(),
        ContactEmail: newAccountContactEmail.trim(),
        AccountName: newAccountName.trim(),
        Website: newAccountReturnWebsite.trim(),
      };

      console.log('ManageAccountsView: Creating new account', createRequest);

      // Call the API
      const newAccount = await accountService.createAccount(createRequest);

      console.log('ManageAccountsView: Account created successfully', newAccount);

      // Map to AccountSearchResult format
      const newSearchResult: AccountSearchResult = {
        accountId: newAccount.AccountId,
        accountName: newAccount.DName || newAccountName.trim(),
        ownerEmail: newAccount.Email || newAccountContactEmail.trim(),
        accountGroup: newAccount.AccountGroup || '-',
        billpayProcessor: newAccount.BillpayProcessor || '-',
        status: newAccount.Status || '-',
        isParent: isTruthyFlag(newAccount.IsParent),
      };

      // Add to search results
      setSearchResults(prevResults => {
        const updated = [...prevResults, newSearchResult];
        // Update account selector options
        updateAccountSelectorOptions(updated);
        return updated;
      });

      // Mark as searched so results are visible
      setHasSearched(true);

      // Close the form
      handleCancelAddAccount();

      // Optionally show success message (you could add a success state similar to error)
      console.log('ManageAccountsView: New account added to results list');
    } catch (err: any) {
      console.error('ManageAccountsView: Add account failed', err);
      setAccountFormError(err?.message || 'Failed to add account');
    } finally {
      setIsSubmittingAccount(false);
    }
  };

  const handleSelectAccount = async (accountId: string, accountName: string) => {
    if (!canSwitchAccount) {
      console.warn('ManageAccountsView: Cannot switch account - user does not have permission');
      return;
    }
    
    if (!apiService || !storage) {
      console.warn('ManageAccountsView: Cannot switch account - apiService or storage missing');
      return;
    }
     
    try {
      // Pre-load ALL account data types before switching
      const accountService = new AccountPracInfoService(apiService);
      const request: AccountPracInfoRequestDto = {
        AccountId: accountId,
        Types: ['Subscriptions', 'AccountSettings', 'Touchpoints', 'AdminOptions', 'AccountSras'],
      };
      
      // Resolve userStore for auth context
      const { userStore } = resolveAuthContext();

      const accountData = await accountService.getAccountPracInfo(request, {
        userStore,
        storage,
      });
      
      // Save to storage immediately
      const pracStorage = new AccountPracInfoStorage(storage);
      await pracStorage.saveAccountPracInfo(accountId, accountData);
      
      
      // Update active account in store (this persists to storage)
      setActiveAccount(accountId, accountName);
      
      // Dispatch event with preloaded flag
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('progroups:active-account-changed', {
          detail: { accountId, accountName, preloaded: true },
        }));
      }
      
    } catch (error: any) {
      console.error('ManageAccountsView: Failed to pre-load account data:', error);
      // Even if pre-load fails, still switch - shell will try to load
      setActiveAccount(accountId, accountName);
      
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('progroups:active-account-changed', {
          detail: { accountId, accountName, preloaded: false },
        }));
      }
    }
  };

  // Get prototype options from search results
  const prototypeOptions = searchResults.length > 0
    ? searchResults
    : activeAccountId
    ? [{ accountId: activeAccountId, accountName: `Account ${activeAccountId}`, ownerEmail: '', accountGroup: '', billpayProcessor: '', status: '' }]
    : [];

  // The backend returns the group/processor value; show its SkinTypeDef label when one matches.
  const optionLabel = (options: AccountFilterOption[], value: string): string =>
    options.find(option => option.value === value)?.label ?? value;

  const pageNum = parseInt(paging.blocknum, 10) || 0;
  const isPrevDisabled = isLoading || pageNum === 0 || !searchedValues;
  const isNextDisabled = isLoading || !paging.hasMore || !searchedValues;

  const isWeb = Platform.OS === 'web';
  const webDropdownStyle = { ...webControlStyle, width: '100%', height: 28 } as any;

  const cyclePrototype = () => {
    if (prototypeOptions.length === 0) {
      return;
    }
    const currentIndex = prototypeOptions.findIndex(opt => opt.accountId === newAccountPrototype);
    const nextIndex = currentIndex < 0 || currentIndex === prototypeOptions.length - 1 ? 0 : currentIndex + 1;
    setNewAccountPrototype(prototypeOptions[nextIndex].accountId);
  };

  const resolvePrototypeLabel = (): string => {
    const found = prototypeOptions.find(opt => opt.accountId === newAccountPrototype);
    return found ? found.accountName : 'Select Prototype...';
  };

  return (
    <View style={styles.container}>
      {isAddingAccount ? (
        <View style={styles.addAccountContainer}>
          <View style={styles.addAccountGrid}>
            {/* Row 1: Prototype */}
            <View style={styles.addAccountCellCol1}>
              <Text style={styles.addAccountLabel}>Prototype</Text>
            </View>
            <View style={styles.addAccountCellCol2}>
              {isWeb ? (
                React.createElement(
                  'select' as any,
                  {
                    value: newAccountPrototype,
                    onChange: (event: any) => setNewAccountPrototype(String(event?.target?.value ?? '')),
                    style: webDropdownStyle,
                    'aria-label': 'Prototype account',
                  },
                  prototypeOptions.map((option) => (
                    React.createElement('option' as any, { key: option.accountId, value: option.accountId }, option.accountName)
                  ))
                )
              ) : (
                <Pressable onPress={cyclePrototype} style={styles.dropdownBtn} accessibilityRole="button" accessibilityLabel="Prototype account">
                  <Text style={styles.dropdownBtnText} numberOfLines={1}>{resolvePrototypeLabel()}</Text>
                  <Text style={styles.dropdownBtnChevron}>▼</Text>
                </Pressable>
              )}
            </View>
            <View style={styles.addAccountCellCol3}>
              <Pressable
                style={[styles.addAccountSubmitBtn, isSubmittingAccount && styles.addAccountSubmitBtnDisabled]}
                onPress={handleSubmitAddAccount}
                disabled={isSubmittingAccount}
                accessibilityRole="button"
                accessibilityLabel="Add account now"
              >
                {isSubmittingAccount ? (
                  <ActivityIndicator size="small" color={EDITOR_COLORS.actionText} />
                ) : (
                  <Text style={styles.addAccountSubmitBtnText}>Add Now</Text>
                )}
              </Pressable>
            </View>

            {/* Row 2: Contact Name */}
            <View style={styles.addAccountCellCol1}>
              <Text style={styles.addAccountLabel}>Contact Name</Text>
            </View>
            <View style={styles.addAccountCellCol2}>
              <TextInput
                style={styles.addAccountInput}
                value={newAccountContactName}
                onChangeText={setNewAccountContactName}
                autoCapitalize="words"
                placeholder="Contact name"
                placeholderTextColor={EDITOR_COLORS.placeholder}
              />
            </View>
            <View style={styles.addAccountCellCol3} />

            {/* Row 3: Contact Email */}
            <View style={styles.addAccountCellCol1}>
              <Text style={styles.addAccountLabel}>Contact Email</Text>
            </View>
            <View style={styles.addAccountCellCol2}>
              <TextInput
                style={styles.addAccountInput}
                value={newAccountContactEmail}
                onChangeText={setNewAccountContactEmail}
                autoCapitalize="none"
                keyboardType="email-address"
                placeholder="Contact email"
                placeholderTextColor={EDITOR_COLORS.placeholder}
              />
            </View>
            <View style={styles.addAccountCellCol3} />

            {/* Row 4: Account Name */}
            <View style={styles.addAccountCellCol1}>
              <Text style={styles.addAccountLabel}>Account Name</Text>
            </View>
            <View style={styles.addAccountCellCol2}>
              <TextInput
                style={styles.addAccountInput}
                value={newAccountName}
                onChangeText={setNewAccountName}
                autoCapitalize="words"
                placeholder="Account name"
                placeholderTextColor={EDITOR_COLORS.placeholder}
              />
            </View>
            <View style={styles.addAccountCellCol3} />

            {/* Row 5: Return Website */}
            <View style={styles.addAccountCellCol1}>
              <Text style={styles.addAccountLabel}>Return Website</Text>
            </View>
            <View style={styles.addAccountCellCol2}>
              <TextInput
                style={styles.addAccountInput}
                value={newAccountReturnWebsite}
                onChangeText={setNewAccountReturnWebsite}
                autoCapitalize="none"
                keyboardType="url"
                placeholder="Return website URL"
                placeholderTextColor={EDITOR_COLORS.placeholder}
              />
            </View>
            <View style={styles.addAccountCellCol3}>
              <Pressable
                onPress={handleCancelAddAccount}
                accessibilityRole="button"
                accessibilityLabel="Cancel add account"
                style={styles.addAccountCancelBtn}
              >
                <Text style={styles.addAccountCancelText}>Cancel</Text>
              </Pressable>
            </View>

            {accountFormError ? (
              <View style={styles.addAccountErrorRow}>
                <Text style={styles.addAccountErrorText}>{accountFormError}</Text>
              </View>
            ) : null}
          </View>
        </View>
      ) : (
        <ManageAccountsSearchBar
          accountGroupOptions={accountGroupOptions}
          billpayProcessorOptions={billpayProcessorOptions}
          values={searchValues}
          onValuesChange={setSearchValues}
          onSearch={handleSearch}
          isLoading={isLoading}
          onAddNewAccount={handleAddNewAccount}
        />
      )}

      <ScrollView style={styles.resultsContainer}>
        <View style={styles.resultsInner}>
          {error ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : !hasSearched ? (
            <View style={styles.placeholderContainer}>
              <Text style={styles.placeholderText}>
                Enter search criteria and click Search to find accounts
              </Text>
            </View>
          ) : isLoading ? (
            <View style={styles.placeholderContainer}>
              <Text style={styles.placeholderText}>Searching...</Text>
            </View>
          ) : searchResults.length === 0 ? (
            <View style={styles.placeholderContainer}>
              <Text style={styles.placeholderText}>No accounts found</Text>
            </View>
          ) : (
            <View style={styles.tableContainer}>
              {/* Table Header */}
              <View style={styles.tableHeader}>
                <View style={[styles.tableCell, styles.checkmarkCol]}>
                  <Text style={styles.headerText}></Text>
                </View>
                <View style={[styles.tableCell, styles.accountNameCol]}>
                  <Text style={styles.headerText}>Account Name</Text>
                </View>
                <View style={[styles.tableCell, styles.ownerEmailCol]}>
                  <Text style={styles.headerText}>Owner Email</Text>
                </View>
                <View style={[styles.tableCell, styles.accountIdCol]}>
                  <Text style={styles.headerText}>Account Id</Text>
                </View>
                <View style={[styles.tableCell, styles.accountGroupCol]}>
                  <Text style={styles.headerText}>Group</Text>
                </View>
                <View style={[styles.tableCell, styles.billingCol]}>
                  <Text style={styles.headerText}>Processor</Text>
                </View>
                <View style={[styles.tableCell, styles.statusCol]}>
                  <Text style={styles.headerText}>Status</Text>
                </View>
              </View>

              {/* Table Rows */}
              {searchResults.map((result, index) => {
                const isActive = result.accountId === activeAccountId;
                const isClickable = canSwitchAccount && !isActive;
                
                return (
                  <Pressable
                    key={result.accountId}
                    onPress={() => handleSelectAccount(result.accountId, result.accountName)}
                    disabled={!isClickable}
                    style={({ pressed }) => [
                      styles.tableRow,
                      index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd,
                      isClickable && pressed && styles.tableRowHover,
                      !isClickable && styles.tableRowDisabled,
                    ]}
                    // @ts-ignore - hoverStyle is web-only
                    hoverStyle={isClickable ? styles.tableRowHover : undefined}
                    accessibilityRole="button"
                    accessibilityLabel={`Select account ${result.accountName}`}
                  >
                    <View style={[styles.tableCell, styles.checkmarkCol]}>
                      {isActive && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <View style={[styles.tableCell, styles.accountNameCol]}>
                      <Text style={styles.cellText}>{result.accountName}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.ownerEmailCol]}>
                      <Text style={styles.cellText}>{result.ownerEmail}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.accountIdCol]}>
                      <Text style={styles.cellText}>{result.accountId}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.accountGroupCol]}>
                      <Text style={styles.cellText}>{optionLabel(accountGroupOptions, result.accountGroup)}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.billingCol]}>
                      <Text style={styles.cellText}>{optionLabel(billpayProcessorOptions, result.billpayProcessor)}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.statusCol]}>
                      <Text style={[styles.cellText, styles.statusText]}>
                        {result.status}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}

              <View style={styles.pagination}>
                <TouchableOpacity
                  onPress={handlePrevPage}
                  disabled={isPrevDisabled}
                  style={[styles.pageBtn, isPrevDisabled && styles.pageBtnDisabled]}
                  accessibilityLabel="Previous page"
                >
                  <Text style={[styles.pageBtnText, isPrevDisabled && styles.pageBtnTextDisabled]}>← Prev</Text>
                </TouchableOpacity>

                <Text style={styles.pageNum}>Page {pageNum + 1}</Text>

                <TouchableOpacity
                  onPress={handleNextPage}
                  disabled={isNextDisabled}
                  style={[styles.pageBtn, isNextDisabled && styles.pageBtnDisabled]}
                  accessibilityLabel="Next page"
                >
                  <Text style={[styles.pageBtnText, isNextDisabled && styles.pageBtnTextDisabled]}>Next →</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: EDITOR_COLORS.background,
  },
  resultsContainer: {
    flex: 1,
  },
  resultsInner: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  placeholderContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  placeholderText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  errorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    textAlign: 'center',
  },
  tableContainer: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  tableRowEven: {
    backgroundColor: EDITOR_COLORS.background,
  },
  tableRowOdd: {
    backgroundColor: '#f9fafb',
  },
  tableRowHover: {
    backgroundColor: '#eff6ff',
    cursor: Platform.OS === 'web' ? 'pointer' : undefined,
  } as any,
  tableRowDisabled: {
    cursor: Platform.OS === 'web' ? 'default' : undefined,
  } as any,
  tableCell: {
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  checkmarkCol: {
    flex: 0,
    minWidth: 40,
    alignItems: 'center',
  },
  checkmark: {
    fontSize: 16,
    color: EDITOR_COLORS.successText,
  },
  accountNameCol: {
    flex: 3,
    minWidth: 180,
  },
  ownerEmailCol: {
    flex: 3,
    minWidth: 200,
  },
  accountIdCol: {
    flex: 1,
    minWidth: 100,
  },
  accountGroupCol: {
    flex: 1,
    minWidth: 110,
  },
  billingCol: {
    flex: 1,
    minWidth: 180,
  },
  statusCol: {
    flex: 1,
    minWidth: 100,
  },
  headerText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  cellText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  statusText: {},
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingTop: 20,
  },
  pageBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.submitBackground,
  },
  pageBtnDisabled: {
    opacity: 0.4,
  },
  pageBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  pageBtnTextDisabled: {
    color: EDITOR_COLORS.placeholder,
  },
  pageNum: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  // Add New Account Form Styles
  addAccountContainer: {
    width: '100%',
    backgroundColor: EDITOR_COLORS.background,
    borderRadius: 14,
    padding: 16,
    marginBottom: 18,
    alignItems: 'center',
  },
  addAccountGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 650,
    rowGap: 4,
  },
  addAccountCellCol1: {
    width: '20%',
    alignItems: 'flex-end',
    justifyContent: 'center',
    minHeight: 25,
    paddingRight: 10,
  },
  addAccountCellCol2: {
    width: '49%',
    alignItems: 'stretch',
    justifyContent: 'center',
    minHeight: 25,
    paddingRight: 8,
  },
  addAccountCellCol3: {
    width: '20%',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 25,
  },
  addAccountLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  addAccountInput: {
    ...editorText,
    height: 28,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  dropdownBtn: {
    height: 28,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.background,
  },
  dropdownBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
  },
  dropdownBtnChevron: {
    ...editorText,
    fontSize: 11,
    color: EDITOR_COLORS.textMuted,
    marginLeft: 4,
  },
  addAccountSubmitBtn: {
    width: 110,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.action,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addAccountSubmitBtnDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  addAccountSubmitBtnText: {
    ...editorText,
    color: EDITOR_COLORS.actionText,
  },
  addAccountCancelBtn: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  addAccountCancelText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    textDecorationLine: 'underline',
  },
  addAccountErrorRow: {
    width: '100%',
    paddingVertical: 8,
    alignItems: 'center',
  },
  addAccountErrorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
  },
});
